import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr, fmtMoney } from "@/lib/api";
import { PageHeader, Loader, EmptyState } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ValStatus, VAL_STATUSES, fmtDelay, useValMeta } from "@/lib/validation";
import { SubmitValidationDialog } from "@/pages/validation/SubmitValidationDialog";
import { ClipboardCheck, Send } from "lucide-react";
import { toast } from "sonner";

const TITLES = {
  pending: ["Mes validations en attente", "Demandes dont vous êtes validateur à l'étape en cours"],
  done: ["Validations que j'ai effectuées", "Demandes sur lesquelles vous avez pris une décision"],
  submitted: ["Mes demandes soumises", "Demandes dont vous êtes le demandeur"],
  all: ["Toutes les validations", "Toutes les demandes de l'entreprise"],
  history: ["Historique des validations", "Demandes clôturées : approuvées, rejetées ou annulées"],
};
const ALL = "__all";

function Filters({ f, setF, ops }) {
  const sel = (k, ph, opts) => (
    <Select value={f[k] || ALL} onValueChange={(v) => setF({ ...f, [k]: v === ALL ? "" : v })}>
      <SelectTrigger className="w-44" data-testid={`val-filter-${k}`}><SelectValue placeholder={ph} /></SelectTrigger>
      <SelectContent><SelectItem value={ALL}>{ph}</SelectItem>{opts.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  );
  const modules = [...new Set(ops.map((o) => o.module))];
  return (
    <div className="flex flex-wrap gap-2 mb-4">
      {sel("module", "Tous les modules", modules.map((m) => [m, m]))}
      {sel("op_type", "Tous les types", ops.map((o) => [o.key, o.label]))}
      {sel("statut", "Tous les statuts", VAL_STATUSES.map((s) => [s, s]))}
      {sel("priorite", "Toutes priorités", ["Basse", "Normale", "Haute", "Urgente"].map((s) => [s, s]))}
      <Input className="w-44" placeholder="Demandeur" value={f.demandeur || ""} onChange={(e) => setF({ ...f, demandeur: e.target.value })} data-testid="val-filter-demandeur" />
      <Input type="date" className="w-40" value={f.date_from || ""} onChange={(e) => setF({ ...f, date_from: e.target.value })} data-testid="val-filter-from" />
      <Input type="date" className="w-40" value={f.date_to || ""} onChange={(e) => setF({ ...f, date_to: e.target.value })} data-testid="val-filter-to" />
    </div>
  );
}

export default function ValidationList({ scope }) {
  const meta = useValMeta();
  const nav = useNavigate();
  const [f, setF] = useState({});
  const [rows, setRows] = useState(null);
  const [submitOpen, setSubmitOpen] = useState(false);
  const load = () => { api.get("/validations/requests", { params: { scope, ...f } }).then((r) => setRows(r.data)).catch((e) => { toast.error(apiErr(e)); setRows([]); }); };
  useEffect(() => { setRows(null); load(); }, [scope, JSON.stringify(f)]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!meta) return <Loader />;
  const [title, sub] = TITLES[scope];
  return (
    <div data-testid={`validation-list-${scope}`}>
      <PageHeader title={title} subtitle={sub}>
        {meta.perms.includes("submit") && <Button onClick={() => setSubmitOpen(true)} className="bg-brand-600 hover:bg-brand-700 text-white" data-testid="val-open-submit"><Send className="h-4 w-4 mr-1.5" />Soumettre une opération</Button>}
      </PageHeader>
      <Filters f={f} setF={setF} ops={meta.ops} />
      {rows === null ? <Loader /> : rows.length === 0 ? <EmptyState icon={ClipboardCheck} title="Aucune demande" subtitle="Rien à afficher avec ces filtres." /> : (
        <div className="rounded-xl border bg-white overflow-x-auto scrollbar-thin">
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><TableHead>Référence</TableHead><TableHead>Type</TableHead><TableHead>Module</TableHead><TableHead>Objet</TableHead><TableHead>Demandeur</TableHead><TableHead className="text-right">Montant</TableHead><TableHead>Soumise le</TableHead><TableHead>Étape</TableHead><TableHead>Statut</TableHead><TableHead>Délai</TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} className="cursor-pointer hover:bg-slate-50" onClick={() => nav(`/validations/demande/${r.id}`)} data-testid={`val-row-${r.id}`}>
                  <TableCell className="font-mono text-xs text-brand-700 whitespace-nowrap">{r.numero}</TableCell>
                  <TableCell className="text-sm whitespace-nowrap">{r.op_label} <span className="text-slate-400">{r.source_ref}</span></TableCell>
                  <TableCell className="text-sm">{r.module}</TableCell>
                  <TableCell className="text-sm max-w-[16rem] truncate">{r.objet}</TableCell>
                  <TableCell className="text-sm whitespace-nowrap">{r.demandeur_name}</TableCell>
                  <TableCell className="text-right font-mono text-sm whitespace-nowrap">{r.montant != null ? fmtMoney(r.montant) : "—"}</TableCell>
                  <TableCell className="text-xs whitespace-nowrap">{new Date(r.created_at).toLocaleString("fr-FR")}</TableCell>
                  <TableCell className="text-xs whitespace-nowrap">{r.etape}</TableCell>
                  <TableCell><ValStatus s={r.statut} /></TableCell>
                  <TableCell className={`text-xs whitespace-nowrap ${r.elapsed_h > 48 && !["Approuvée", "Rejetée", "Annulée"].includes(r.statut) ? "text-rose-600 font-semibold" : ""}`}>{fmtDelay(r.elapsed_h)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <SubmitValidationDialog open={submitOpen} onOpenChange={setSubmitOpen} ops={meta.ops} onDone={(id) => nav(`/validations/demande/${id}`)} />
    </div>
  );
}
